import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import HttpError, { isHttpError } from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import { AddIcon, EditIcon, InfoIcon } from '@common/utils/icons/icons';
import { sortByDate } from '@common/utils/date';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectAssignableRobots,
  selectTorizonPackages,
  selectTorizonVersionSets,
} from '../../store/general/general-slice-selectors';
import {
  assignTorizonVersionSet,
  getTorizonPackages,
  getTorizonVersionSets,
  patchTorizonVersionSetById,
} from '../../store/general/general-actions';
import {
  resetAssignableRobotsRestState,
  resetTorizonVersionSetsRestState,
} from '../../store/general/general-slice';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';
import TorizonVersionSetForm from './TorizonVersionSetForm';
import TorizonRobotAssignmentTable from './TorizonRobotAssignmentTable';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import { getTorizonPackageLabel } from 'models/general/versions/torizon-package';

const TorizonVersionSetsPanel: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const torizonSets = useAppSelector((state) => selectTorizonVersionSets(state));
  const torizonPackages = useAppSelector((state) => selectTorizonPackages(state));
  const assignableRobots = useAppSelector((state) => selectAssignableRobots(state));

  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [selectedRobotIds, setSelectedRobotIds] = useState<string[]>([]);
  const [editedSet, setEditedSet] = useState<Partial<TorizonVersionSet> | null>(null);
  const [isConfirmAssignDialogOpen, setIsConfirmAssignDialogOpen] = useState(false);

  useEffect(() => {
    if (torizonSets.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getTorizonVersionSets());
    }
  }, [dispatch, torizonSets.rest.getStatus]);

  useEffect(() => {
    if (torizonPackages.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getTorizonPackages());
    }
  }, [dispatch, torizonPackages.rest.getStatus]);

  const sortedSets = useMemo(
    () => [...(torizonSets.data ?? [])].sort((a, b) => sortByDate(a.dateUpdated, b.dateUpdated)),
    [torizonSets.data],
  );

  const packageLabel = (packageId: string | null | undefined) =>
    getTorizonPackageLabel(packageId, torizonPackages.data);

  const packageRows = (versionSet: TorizonVersionSet) => [
    // TODO-torizon-bootloader: no bootloader row while Torizon ships it inside the OS image.
    { label: t('table-header-torizon-os'), text: packageLabel(versionSet.osPackageId) },
    { label: t('table-header-torizon-docker'), text: packageLabel(versionSet.dockerPackageId) },
  ];

  const dropDefaultClickHandler = (versionSet: TorizonVersionSet) => {
    dispatch(patchTorizonVersionSetById({ id: versionSet.id, data: { isDefault: false } }));
  };

  const confirmAssignHandler = () => {
    if (selectedSetId != null) {
      dispatch(assignTorizonVersionSet({ id: selectedSetId, robotIds: selectedRobotIds }));
    }
    setIsConfirmAssignDialogOpen(false);
    setSelectedRobotIds([]);
  };

  const closePostHandler = () => {
    dispatch(
      resetTorizonVersionSetsRestState({ postStatus: HttpFetchStatus.Idle, postError: null }),
    );
  };

  const closePatchHandler = () => {
    dispatch(
      resetTorizonVersionSetsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }),
    );
  };

  const closeDeleteHandler = () => {
    dispatch(
      resetTorizonVersionSetsRestState({ deleteStatus: HttpFetchStatus.Idle, deleteError: null }),
    );
  };

  const closeAssignHandler = () => {
    dispatch(resetAssignableRobotsRestState({ postStatus: HttpFetchStatus.Idle, postError: null }));
  };

  const errorMessages = (error: HttpError | ValidationResult[] | null) =>
    error != null && isHttpError(error) ? [t(error.message)] : undefined;

  const defaultSets = sortedSets.filter((versionSet) => versionSet.isDefault);
  const effectiveDefault = defaultSets[0] ?? null;

  const selectedSet = sortedSets.find((versionSet) => versionSet.id === selectedSetId) ?? null;

  const unassignedRobots = (assignableRobots.data ?? []).filter(
    (robot) => robot.versionSetId == null,
  );

  const assignAllToDefaultClickHandler = () => {
    if (effectiveDefault == null) return;
    setSelectedSetId(effectiveDefault.id);
    setSelectedRobotIds(unassignedRobots.map((robot) => robot.id));
    setIsConfirmAssignDialogOpen(true);
  };

  return (
    <React.Fragment>
      <Stack spacing={2} height="100%">
        {torizonPackages.rest.getStatus === HttpFetchStatus.Loading && (
          <CustomAlert severity="info" header={t('loading-versions')} />
        )}
        {torizonPackages.rest.getStatus === HttpFetchStatus.Failed && (
          <CustomAlert severity="error" header={t('error-torizon-catalogue-unavailable')} />
        )}
        {unassignedRobots.length > 0 && (
          <CustomAlert
            severity="warning"
            header={t('warning-torizon-unassigned-robots', { count: unassignedRobots.length })}
            textMessages={unassignedRobots.map((robot) => `${robot.name} (${robot.serialNumber})`)}
            action={
              effectiveDefault != null && (
                <Button
                  color="inherit"
                  size="small"
                  data-testid="button-assign-all-to-default-torizon-version-set"
                  onClick={assignAllToDefaultClickHandler}
                >
                  {t('button-assign-all-to-default-torizon-version-set', {
                    name: effectiveDefault.name,
                  })}
                </Button>
              )
            }
          />
        )}
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h3">{t('title-torizon-default-version-set')}</Typography>
                {effectiveDefault != null && (
                  <TooltipIconButton
                    icon={<EditIcon />}
                    tooltip={t('button-edit')}
                    data-testid="button-edit-default-torizon-version-set"
                    onClick={() => setEditedSet({ ...effectiveDefault })}
                  />
                )}
              </Stack>

              {effectiveDefault == null ? (
                <CustomAlert severity="error" header={t('error-torizon-no-default-version-set')} />
              ) : (
                <React.Fragment>
                  <Typography>{effectiveDefault.name}</Typography>
                  <CustomLabelTextTable rows={packageRows(effectiveDefault)} />
                </React.Fragment>
              )}

              {defaultSets.length > 1 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {defaultSets.slice(1).map((versionSet) => (
                    <Chip
                      key={versionSet.id}
                      label={versionSet.name}
                      data-testid="chip-extra-default-torizon-version-set"
                      onDelete={() => dropDefaultClickHandler(versionSet)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h3">{t('title-torizon-version-sets')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  data-testid="button-create-torizon-version-set"
                  onClick={() => setEditedSet({})}
                >
                  {t('button-create-torizon-version-set')}
                </Button>
              </Stack>
              <Divider />
              <List disablePadding>
                {sortedSets.map((versionSet) => (
                  <ListItemButton
                    key={versionSet.id}
                    data-testid="torizon-version-set-row"
                    selected={versionSet.id === selectedSetId}
                    onClick={() => {
                      setSelectedSetId(versionSet.id);
                      setSelectedRobotIds([]);
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      spacing={1}
                    >
                      <Stack>
                        <Typography>{versionSet.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('label-torizon-version-set-robots', { count: versionSet.robotCount })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center">
                        <TooltipIconButton
                          icon={<InfoIcon />}
                          tooltip={packageRows(versionSet)
                            .map((row) => `${row.label}: ${row.text}`)
                            .join(' · ')}
                        />
                        <TooltipIconButton
                          icon={<EditIcon />}
                          tooltip={t('button-edit')}
                          data-testid="button-edit-torizon-version-set"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditedSet({ ...versionSet });
                          }}
                        />
                      </Stack>
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            <TorizonRobotAssignmentTable
              versionSets={sortedSets}
              selectedRobotIds={selectedRobotIds}
              onSelectionChange={setSelectedRobotIds}
              selectedSetId={selectedSetId}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            data-testid="button-assign-torizon-version-set"
            disabled={selectedSet == null || selectedRobotIds.length === 0}
            onClick={() => setIsConfirmAssignDialogOpen(true)}
          >
            {t('button-assign-torizon-version-set', { name: selectedSet?.name ?? '' })}
          </Button>
        </Stack>
      </Stack>

      {editedSet != null && (
        <TorizonVersionSetForm versionSet={editedSet} onClose={() => setEditedSet(null)} />
      )}

      <CustomConfirmAbortDialog
        open={isConfirmAssignDialogOpen}
        title={t('title-confirm-assign-torizon-version-set')}
        text={t('info-confirm-assign-torizon-version-set', {
          name: selectedSet?.name ?? '',
          count: selectedRobotIds.length,
        })}
        confirmButton={t('button-confirm')}
        abortButton={t('button-cancel')}
        onAbort={() => setIsConfirmAssignDialogOpen(false)}
        onConfirm={confirmAssignHandler}
      />

      <CustomSnackbar
        open={torizonSets.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-version')}
        onClose={closePostHandler}
      />
      <CustomSnackbar
        open={torizonSets.rest.postStatus === HttpFetchStatus.Failed}
        title={t('error-creating-version')}
        onClose={closePostHandler}
        severity="error"
        textMessages={errorMessages(torizonSets.rest.postError)}
      />
      <CustomSnackbar
        open={torizonSets.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-version')}
        onClose={closePatchHandler}
      />
      <CustomSnackbar
        open={torizonSets.rest.patchStatus === HttpFetchStatus.Failed}
        title={t('error-patching-version')}
        onClose={closePatchHandler}
        severity="error"
        textMessages={errorMessages(torizonSets.rest.patchError)}
      />
      <CustomSnackbar
        open={torizonSets.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-version')}
        onClose={closeDeleteHandler}
      />
      <CustomSnackbar
        open={torizonSets.rest.deleteStatus === HttpFetchStatus.Failed}
        title={t('error-deleting-version')}
        onClose={closeDeleteHandler}
        severity="error"
        textMessages={errorMessages(torizonSets.rest.deleteError)}
      />
      <CustomSnackbar
        open={assignableRobots.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-assigning-torizon-version-set')}
        onClose={closeAssignHandler}
      />
      <CustomSnackbar
        open={assignableRobots.rest.postStatus === HttpFetchStatus.Failed}
        title={t('error-assigning-torizon-version-set')}
        onClose={closeAssignHandler}
        severity="error"
        textMessages={errorMessages(assignableRobots.rest.postError)}
      />
    </React.Fragment>
  );
};

export default TorizonVersionSetsPanel;
